import { useCallback, useEffect, useState } from 'react'
import {
  Building2, Search, X, Loader2, SquarePen, Trash2,
  Users, LayoutGrid, Cpu, PauseCircle, PlayCircle, AlertTriangle,
} from 'lucide-react'
import { Tooltip } from '../../../components/Tooltip'
import { toast } from 'react-hot-toast'
import { platformOrgService } from '../../../services/platformOrgService'
import type {
  OrganizationStats,
  SuspendOrgResponse, DeleteOrgResponse, BulkUnsuspendOrgResponse, ProvisionOrgResponse,
} from '../../../types/api.types'
import { LoadErrorState } from '../../../components/LoadErrorState'
import { EmptyState } from '../../admin/components/EmptyState'

const ORG_TYPE_OPTIONS = ['HOTEL', 'RESTAURANT', 'SECURITY'] as const
type OrgType = typeof ORG_TYPE_OPTIONS[number]

const ORG_TYPE_BADGE: Record<OrgType, string> = {
  HOTEL: 'bg-blue-100 text-blue-700',
  RESTAURANT: 'bg-amber-100 text-amber-700',
  SECURITY: 'bg-green-100 text-green-700',
}
const ORG_TYPE_LABEL: Record<OrgType, string> = {
  HOTEL: 'Hotel',
  RESTAURANT: 'Restaurant',
  SECURITY: 'Security',
}

function OrgTypeBadge({ type }: { type?: string | null }): JSX.Element {
  if (!type) return <span className="text-[12px] text-gray-400">—</span>
  const cls = ORG_TYPE_BADGE[type as OrgType] ?? 'bg-gray-100 text-gray-500'
  const label = ORG_TYPE_LABEL[type as OrgType] ?? type
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>
}

// ── Suspend confirmation modal ──
function SuspendModal({
  org,
  onConfirm,
  onCancel,
}: {
  org: OrganizationStats
  onConfirm: () => Promise<void>
  onCancel: () => void
}): JSX.Element {
  const [submitting, setSubmitting] = useState(false)
  async function handle() {
    setSubmitting(true)
    try { await onConfirm() } finally { setSubmitting(false) }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-amber-100">
          <PauseCircle size={20} className="text-amber-600" />
        </div>
        <h2 className="text-[16px] font-bold text-gray-900">Suspend organisation?</h2>
        <p className="mt-1 text-[13px] text-gray-600">
          <span className="font-semibold">{org.name}</span> will be taken offline.
        </p>
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-700">
          <span className="font-semibold">{org.user_count} user{org.user_count !== 1 ? 's' : ''}</span>
          {' '}will be force-logged out immediately. They cannot log back in until the organisation is unsuspended.
        </div>
        <div className="mt-5 flex gap-3">
          <button type="button" onClick={handle} disabled={submitting}
            className="flex-1 rounded-lg bg-amber-500 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50">
            {submitting ? 'Suspending…' : 'Yes, suspend'}
          </button>
          <button type="button" onClick={onCancel} disabled={submitting}
            className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Unsuspend confirmation modal ──
function UnsuspendModal({
  org, onConfirm, onCancel,
}: { org: OrganizationStats; onConfirm: () => Promise<void>; onCancel: () => void }): JSX.Element {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  async function handle() {
    setSubmitting(true); setError('')
    try { await onConfirm() }
    catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Failed to unsuspend — please try again')
    }
    finally { setSubmitting(false) }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-green-100">
          <PlayCircle size={20} className="text-green-600" />
        </div>
        <h2 className="text-[16px] font-bold text-gray-900">Unsuspend organisation?</h2>
        <p className="mt-1 text-[13px] text-gray-600">
          <span className="font-semibold">{org.name}</span> will come back online. Users will be able to log in again.
        </p>
        {error && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-600">{error}</p>
        )}
        <div className="mt-5 flex gap-3">
          <button type="button" onClick={handle} disabled={submitting}
            className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50">
            {submitting ? 'Unsuspending…' : 'Yes, unsuspend'}
          </button>
          <button type="button" onClick={onCancel} disabled={submitting}
            className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ── 2-step delete modal ──
function DeleteModal({
  org, onConfirm, onCancel,
}: { org: OrganizationStats; onConfirm: () => Promise<DeleteOrgResponse>; onCancel: () => void }): JSX.Element {
  const [step, setStep] = useState<1 | 2>(1)
  const [typed, setTyped] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<DeleteOrgResponse | null>(null)
  const matches = typed === org.name

  async function handle() {
    if (!matches) return
    setSubmitting(true)
    try {
      const res = await onConfirm()
      setResult(res)
    } catch {
      setSubmitting(false)
    }
  }

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-green-100">
            <Trash2 size={18} className="text-green-600" />
          </div>
          <h2 className="text-[15px] font-bold text-gray-900">Deleted successfully</h2>
          <p className="mt-1 text-[13px] text-gray-500">{result.message}</p>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            {[['Users', result.deleted.users_deleted], ['Properties', result.deleted.properties_deleted], ['Devices', result.deleted.devices_deleted]].map(([label, count]) => (
              <div key={label as string} className="rounded-lg bg-gray-50 p-2">
                <p className="text-[18px] font-bold text-gray-900">{count}</p>
                <p className="text-[11px] text-gray-500">{label}</p>
              </div>
            ))}
          </div>
          <button type="button" onClick={onCancel} className="mt-5 w-full rounded-lg bg-gray-900 py-2.5 text-[14px] font-medium text-white hover:opacity-80">
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-red-100">
          <AlertTriangle size={20} className="text-red-600" />
        </div>

        {step === 1 ? (
          <>
            <h2 className="text-[16px] font-bold text-gray-900">Delete organisation?</h2>
            <p className="mt-1 text-[13px] text-gray-600">
              Permanently delete <span className="font-semibold">{org.name}</span>. This will remove:
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              {[['Users', org.user_count, 'bg-red-50 text-red-700'], ['Properties', org.property_count, 'bg-red-50 text-red-700'], ['Devices', org.device_count, 'bg-red-50 text-red-700']].map(([label, count, cls]) => (
                <div key={label as string} className={`rounded-lg p-2 ${cls}`}>
                  <p className="text-[18px] font-bold">{count}</p>
                  <p className="text-[11px]">{label}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[12px] font-semibold text-red-600">This action is irreversible.</p>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => setStep(2)}
                className="flex-1 rounded-lg bg-red-600 py-2.5 text-[14px] font-semibold text-white hover:bg-red-700">
                Continue
              </button>
              <button type="button" onClick={onCancel}
                className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-[16px] font-bold text-gray-900">Confirm deletion</h2>
            <p className="mt-1 text-[13px] text-gray-600">
              Type the organisation name to confirm:
            </p>
            <p className="mt-1 rounded bg-gray-100 px-2 py-1 font-mono text-[13px] text-gray-800">{org.name}</p>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className={`mt-3 w-full rounded-lg border px-3 py-2.5 text-[14px] outline-none focus:border-red-400 ${typed && !matches ? 'border-red-300' : 'border-gray-200'}`}
              placeholder="Type exact name..."
              autoFocus
            />
            <div className="mt-4 flex gap-3">
              <button type="button" onClick={handle} disabled={!matches || submitting}
                className="flex-1 rounded-lg bg-red-600 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:bg-red-700 disabled:opacity-40">
                {submitting ? 'Deleting…' : 'Permanently Delete'}
              </button>
              <button type="button" onClick={onCancel} disabled={submitting}
                className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Bulk unsuspend modal ──
function BulkUnsuspendModal({
  suspendedCount,
  onCancel,
}: {
  suspendedCount: number
  onCancel: () => void
}): JSX.Element {
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<BulkUnsuspendOrgResponse | null>(null)
  const [error, setError] = useState('')

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-green-100">
            <PlayCircle size={20} className="text-green-600" />
          </div>
          <h2 className="text-[15px] font-bold text-gray-900">Bulk unsuspend complete</h2>
          <div className="mt-3 grid grid-cols-2 gap-2 text-center">
            <div className="rounded-lg bg-green-50 p-3">
              <p className="text-[22px] font-bold text-green-700">{result.unsuspended_count}</p>
              <p className="text-[11px] text-green-600">Restored</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-[22px] font-bold text-gray-700">{result.already_active_count}</p>
              <p className="text-[11px] text-gray-500">Already active</p>
            </div>
          </div>
          {result.organizations.length > 0 && (
            <div className="mt-3 max-h-32 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              {result.organizations.map((o) => (
                <p key={o.organization_id} className="py-0.5 text-[12px] text-gray-700">— {o.name}</p>
              ))}
            </div>
          )}
          {result.unsuspended_count > 0 && (
            <p className="mt-3 text-[12px] text-gray-500">Org admins have been notified by email.</p>
          )}
          <button type="button" onClick={onCancel}
            className="mt-5 w-full rounded-lg bg-gray-900 py-2.5 text-[14px] font-medium text-white hover:opacity-80">
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-green-100">
          <PlayCircle size={20} className="text-green-600" />
        </div>
        <h2 className="text-[16px] font-bold text-gray-900">Unsuspend all organisations?</h2>
        <p className="mt-1 text-[13px] text-gray-600">
          This will restore <span className="font-semibold">{suspendedCount} suspended organisation{suspendedCount !== 1 ? 's' : ''}</span> in one operation. Users will be able to log in again immediately.
        </p>
        {error && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-600">{error}</p>
        )}
        <div className="mt-5 flex gap-3">
          <button type="button" onClick={async () => {
            setSubmitting(true); setError('')
            try {
              const res = await platformOrgService.bulkUnsuspend()
              setResult(res)
            } catch (e) {
              const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
              setError(detail || 'Bulk unsuspend failed — please try again')
            }
            finally { setSubmitting(false) }
          }} disabled={submitting}
            className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50">
            {submitting ? 'Restoring…' : 'Unsuspend All'}
          </button>
          <button type="button" onClick={onCancel} disabled={submitting}
            className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Org edit modal (PATCH — name / type / logo only) ──
function OrgEditModal({
  org, onSaved, onClose,
}: {
  org: OrganizationStats
  onSaved: (updated: OrganizationStats) => void
  onClose: () => void
}): JSX.Element {
  const [name, setName] = useState(org.name)
  const [orgType, setOrgType] = useState(org.organization_type ?? '')
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit() {
    setError('')
    const updates: { name?: string; organization_type?: string | null } = {}
    if (name.trim() !== org.name) updates.name = name.trim()
    if (orgType !== (org.organization_type ?? '')) updates.organization_type = orgType || null
    const hasMetaChanges = Object.keys(updates).length > 0
    if (!hasMetaChanges && !logoFile) { setError('No changes to save'); return }
    setSubmitting(true)
    try {
      let updated = org
      if (hasMetaChanges) {
        updated = await platformOrgService.update(org.id, updates)
      }
      if (logoFile) {
        updated = await platformOrgService.uploadLogo(org.id, logoFile)
      }
      toast.success('Organisation updated')
      onSaved(updated)
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Update failed')
    } finally { setSubmitting(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-[15px] font-bold text-gray-900">Edit Organisation</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-[13px] font-medium text-gray-700">Name*</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-green"
              placeholder="Organisation name" />
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-medium text-gray-700">Type</label>
            <select value={orgType} onChange={(e) => setOrgType(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-green">
              <option value="">— Not set —</option>
              {ORG_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{ORG_TYPE_LABEL[t]}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-medium text-gray-700">
              Logo <span className="text-gray-400">(PNG/JPEG/WEBP, max 5 MB)</span>
            </label>
            {org.logo_url && !logoFile && (
              <img src={org.logo_url} alt="current logo"
                className="mb-2 h-10 w-auto rounded border border-gray-100 object-contain" />
            )}
            <input type="file" accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] text-gray-600 file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-2 file:py-1 file:text-[12px] file:font-medium" />
          </div>
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-600">{error}</p>}
        </div>
        <div className="mt-5 flex gap-3">
          <button type="button" onClick={handleSubmit} disabled={submitting}
            className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black hover:opacity-90 disabled:opacity-50">
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
          <button type="button" onClick={onClose}
            className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Provision org modal (org + admin + logo in one call) ──
interface ProvisionForm {
  orgName: string; orgType: string
  adminName: string; adminEmail: string; adminPassword: string; adminPhone: string
}
const EMPTY_PROVISION: ProvisionForm = {
  orgName: '', orgType: '', adminName: '', adminEmail: '', adminPassword: '', adminPhone: '',
}

function ProvisionOrgModal({ onDone, onClose }: { onDone: () => void; onClose: () => void }): JSX.Element {
  const [form, setForm] = useState<ProvisionForm>(EMPTY_PROVISION)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [errors, setErrors] = useState<Partial<ProvisionForm & { logo: string }>>({})
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ProvisionOrgResponse | null>(null)

  function validate(): boolean {
    const e: Partial<ProvisionForm & { logo: string }> = {}
    if (!form.orgName.trim()) e.orgName = 'Required'
    if (!form.adminName.trim()) e.adminName = 'Required'
    if (!form.adminEmail.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(form.adminEmail)) e.adminEmail = 'Valid email required'
    if (!form.adminPassword || form.adminPassword.length < 8 || !/[a-zA-Z]/.test(form.adminPassword) || !/[0-9]/.test(form.adminPassword)) {
      e.adminPassword = 'Min 8 chars with letters and numbers'
    }
    if (!logoFile) e.logo = 'Logo is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit() {
    if (!validate()) return
    setSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('name', form.orgName.trim())
      fd.append('admin_name', form.adminName.trim())
      fd.append('admin_email', form.adminEmail.trim())
      fd.append('admin_password', form.adminPassword)
      if (form.adminPhone.trim()) fd.append('admin_phone', form.adminPhone.trim())
      if (form.orgType) fd.append('organization_type', form.orgType)
      fd.append('logo', logoFile!)
      const res = await platformOrgService.provision(fd)
      setResult(res)
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErrors((p) => ({ ...p, orgName: detail || 'Provision failed' }))
      setSubmitting(false)
    }
  }

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-green-100">
            <Building2 size={20} className="text-green-600" />
          </div>
          <h2 className="text-[15px] font-bold text-gray-900">Organisation provisioned</h2>
          <p className="mt-1 text-[13px] text-gray-600">
            <span className="font-semibold">{result.organization.name}</span> is ready.
          </p>
          <div className="mt-3 space-y-1.5 rounded-lg border border-gray-100 bg-gray-50 p-3 text-[12px] text-gray-600">
            <p><span className="font-medium">Admin:</span> {result.admin.name}</p>
            <p><span className="font-medium">Email:</span> {result.admin.email}</p>
          </div>
          <p className="mt-2 text-[12px] text-gray-500">
            Login credentials sent to <span className="font-medium">{result.admin.email}</span>.
          </p>
          <button type="button" onClick={() => { onDone(); onClose() }}
            className="mt-5 w-full rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black hover:opacity-90">
            Done
          </button>
        </div>
      </div>
    )
  }

  const inp = (field: keyof typeof errors) =>
    `w-full rounded-lg border px-3 py-2.5 text-[14px] outline-none focus:border-brand-green ${errors[field] ? 'border-red-400' : 'border-gray-200'}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-6 pt-6 pb-4">
          <h2 className="text-[16px] font-bold text-gray-900">Provision Organisation</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div>
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-gray-400">Organisation</h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Name*</label>
                <input value={form.orgName} onChange={(e) => { setForm(p => ({ ...p, orgName: e.target.value })); setErrors(p => ({ ...p, orgName: undefined })) }}
                  className={inp('orgName')} placeholder="e.g. Al Faisaliah Group" />
                {errors.orgName && <p className="mt-1 text-[12px] text-red-500">{errors.orgName}</p>}
              </div>
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Type</label>
                <select value={form.orgType} onChange={(e) => setForm(p => ({ ...p, orgType: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-green">
                  <option value="">— Not set —</option>
                  {ORG_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{ORG_TYPE_LABEL[t]}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Logo* <span className="text-gray-400">(PNG/JPEG/WEBP, max 5 MB)</span></label>
                <input type="file" accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => { setLogoFile(e.target.files?.[0] ?? null); setErrors(p => ({ ...p, logo: undefined })) }}
                  className={`w-full rounded-lg border px-3 py-2 text-[13px] text-gray-600 file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-2 file:py-1 file:text-[12px] file:font-medium ${errors.logo ? 'border-red-400' : 'border-gray-200'}`} />
                {errors.logo && <p className="mt-1 text-[12px] text-red-500">{errors.logo}</p>}
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-gray-400">Tenant Admin Account</h3>
            <div className="space-y-3">
              {([
                ['Admin Name*', 'adminName', 'text', 'Full name'],
                ['Admin Email*', 'adminEmail', 'email', 'admin@company.com'],
                ['Admin Phone', 'adminPhone', 'tel', '+923001234567'],
              ] as [string, keyof ProvisionForm, string, string][]).map(([label, field, type, ph]) => (
                <div key={field}>
                  <label className="mb-1 block text-[13px] font-medium text-gray-700">{label}</label>
                  <input type={type} value={form[field]}
                    onChange={(e) => { setForm(p => ({ ...p, [field]: e.target.value })); setErrors(p => ({ ...p, [field]: undefined })) }}
                    className={inp(field as keyof typeof errors)} placeholder={ph} />
                  {errors[field as keyof typeof errors] && <p className="mt-1 text-[12px] text-red-500">{errors[field as keyof typeof errors]}</p>}
                </div>
              ))}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Password*</label>
                <input type="password" value={form.adminPassword}
                  onChange={(e) => { setForm(p => ({ ...p, adminPassword: e.target.value })); setErrors(p => ({ ...p, adminPassword: undefined })) }}
                  className={inp('adminPassword')} placeholder="Min 8 chars, letters + numbers" />
                {errors.adminPassword && <p className="mt-1 text-[12px] text-red-500">{errors.adminPassword}</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex gap-3 border-t border-gray-100 bg-white px-6 pb-6 pt-4">
          <button type="button" onClick={handleSubmit} disabled={submitting}
            className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black hover:opacity-90 disabled:opacity-50">
            {submitting ? 'Provisioning…' : 'Provision Organisation'}
          </button>
          <button type="button" onClick={onClose}
            className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ──
export function OrganizationsPage(): JSX.Element {
  const [orgs, setOrgs] = useState<OrganizationStats[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingOrg, setEditingOrg] = useState<OrganizationStats | null>(null)

  const [suspendTarget, setSuspendTarget] = useState<OrganizationStats | null>(null)
  const [unsuspendTarget, setUnsuspendTarget] = useState<OrganizationStats | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<OrganizationStats | null>(null)
  const [showBulkUnsuspend, setShowBulkUnsuspend] = useState(false)

  const loadData = useCallback(async (type = filterType, status = filterStatus) => {
    setLoading(true); setLoadError(false)
    try {
      const params: { organization_type?: string; is_active?: boolean } = {}
      if (type) params.organization_type = type
      if (status !== '') params.is_active = status === 'true'
      setOrgs(await platformOrgService.getAll(params))
    }
    catch { setLoadError(true) }
    finally { setLoading(false) }
   
  }, [filterType, filterStatus])

  useEffect(() => { loadData() }, [loadData])

  const filtered = search.trim()
    ? orgs.filter((o) =>
        o.name.toLowerCase().includes(search.toLowerCase()) ||
        (o.organization_type ?? '').toLowerCase().includes(search.toLowerCase()),
      )
    : orgs

  async function handleProvisionDone() {
    await loadData()
  }

  function handleEditSaved(updated: OrganizationStats) {
    setOrgs((prev) => prev.map((o) => o.id === updated.id ? updated : o))
    setEditingOrg(null)
  }

  async function handleSuspend() {
    if (!suspendTarget) return
    const res: SuspendOrgResponse = await platformOrgService.suspend(suspendTarget.id)
    toast.success(`'${res.organization_name}' suspended. ${res.users_logged_out} users logged out. Org admins notified by email.`)
    setSuspendTarget(null); await loadData()
  }

  async function handleUnsuspend() {
    if (!unsuspendTarget) return
    const res: SuspendOrgResponse = await platformOrgService.unsuspend(unsuspendTarget.id)
    toast.success(`'${res.organization_name}' restored. Org admins notified by email.`)
    setUnsuspendTarget(null); await loadData()
  }

  async function handleDelete(): Promise<DeleteOrgResponse> {
    if (!deleteTarget) throw new Error('No target')
    const res = await platformOrgService.delete(deleteTarget.id)
    await loadData()
    return res
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-brand-green" />
        <span className="ml-2 text-sm text-gray-500">Loading organisations…</span>
      </div>
    )
  }

  if (loadError) {
    return <LoadErrorState message="Couldn't load organisations" onRetry={loadData} />
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Organisations</h1>
        <button type="button" onClick={() => setShowCreateForm(true)}
          className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-70">
          + Create Organisation
        </button>
      </div>

      {/* Search + filters */}
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-4 text-[13px] outline-none focus:border-brand-green" />
          {search && (
            <button type="button" onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          )}
        </div>
        <select value={filterType}
          onChange={(e) => { setFilterType(e.target.value); loadData(e.target.value, filterStatus) }}
          className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-[13px] text-gray-700 outline-none focus:border-brand-green">
          <option value="">All Types</option>
          {ORG_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{ORG_TYPE_LABEL[t]}</option>)}
        </select>
        <select value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); loadData(filterType, e.target.value) }}
          className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-[13px] text-gray-700 outline-none focus:border-brand-green">
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Suspended</option>
        </select>
      </div>

      {/* Stats bar */}
      <div className="mb-4 flex flex-wrap items-center gap-4 text-[13px] text-gray-500">
        <span>{orgs.length} total</span>
        <span className="text-green-600">{orgs.filter((o) => o.is_active).length} active</span>
        {orgs.some((o) => !o.is_active) && (
          <>
            <span className="text-amber-600">{orgs.filter((o) => !o.is_active).length} suspended</span>
            <button type="button" onClick={() => setShowBulkUnsuspend(true)}
              className="ml-auto rounded-lg border border-green-300 bg-green-50 px-3 py-1 text-[12px] font-semibold text-green-700 transition-colors hover:bg-green-100">
              Unsuspend All
            </button>
          </>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Building2} message="No organisations found" hint={search ? 'Try a different search' : undefined} />
      ) : (
        <div className="space-y-3">
          {filtered.map((org) => (
            <div key={org.id}
              className={`rounded-xl border bg-white px-5 py-4 transition-colors ${org.is_active ? 'border-gray-200 hover:border-gray-300' : 'border-amber-200 bg-amber-50/30'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  {/* Name + status badge */}
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-[15px] font-semibold text-gray-900">{org.name}</p>
                    {org.is_active ? (
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-[11px] font-medium text-green-700">Active</span>
                    ) : (
                      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">Suspended</span>
                    )}
                    <OrgTypeBadge type={org.organization_type} />
                  </div>

                  {/* Stats chips */}
                  <div className="mt-2 flex flex-wrap gap-3">
                    <span className="flex items-center gap-1 text-[12px] text-gray-500">
                      <Users size={12} />{org.user_count} users
                    </span>
                    <span className="flex items-center gap-1 text-[12px] text-gray-500">
                      <LayoutGrid size={12} />{org.property_count} properties
                    </span>
                    <span className="flex items-center gap-1 text-[12px] text-gray-500">
                      <Cpu size={12} />{org.device_count} devices
                    </span>
                    {org.suspended_at && (
                      <span className="text-[12px] text-amber-600">
                        Suspended {new Date(org.suspended_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-shrink-0 items-center gap-2">
                  <Tooltip label="Edit">
                    <button type="button" onClick={() => setEditingOrg(org)}
                      className="text-brand-green hover:text-brand-green/70">
                      <SquarePen size={16} />
                    </button>
                  </Tooltip>
                  {org.is_active ? (
                    <Tooltip label="Suspend">
                      <button type="button" onClick={() => setSuspendTarget(org)}
                        className="text-amber-500 hover:text-amber-600">
                        <PauseCircle size={17} />
                      </button>
                    </Tooltip>
                  ) : (
                    <Tooltip label="Unsuspend">
                      <button type="button" onClick={() => setUnsuspendTarget(org)}
                        className="text-green-500 hover:text-green-600">
                        <PlayCircle size={17} />
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip label="Delete permanently">
                    <button type="button" onClick={() => setDeleteTarget(org)}
                      className="text-red-400 hover:text-red-600">
                      <Trash2 size={16} />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreateForm && (
        <ProvisionOrgModal
          onDone={handleProvisionDone}
          onClose={() => setShowCreateForm(false)}
        />
      )}
      {editingOrg && (
        <OrgEditModal
          org={editingOrg}
          onSaved={handleEditSaved}
          onClose={() => setEditingOrg(null)}
        />
      )}
      {suspendTarget && (
        <SuspendModal org={suspendTarget} onConfirm={handleSuspend} onCancel={() => setSuspendTarget(null)} />
      )}
      {unsuspendTarget && (
        <UnsuspendModal org={unsuspendTarget} onConfirm={handleUnsuspend} onCancel={() => setUnsuspendTarget(null)} />
      )}
      {deleteTarget && (
        <DeleteModal
          org={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {showBulkUnsuspend && (
        <BulkUnsuspendModal
          suspendedCount={orgs.filter((o) => !o.is_active).length}
          onCancel={() => { setShowBulkUnsuspend(false); loadData() }}
        />
      )}
    </div>
  )
}
